import { useState, useEffect } from "react"
import { useQuery, useMutation } from "urql"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2, Pencil, Play, Code2, X } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { StatusBadge } from "@/components/shared/status-badge"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { JsonViewer } from "@/components/shared/json-viewer"
import { JsonDiff } from "@/components/shared/json-diff"
import { sectionTitleClass } from "@/components/shared/field-label"
import { TransformationsQuery } from "@/lib/graphql/queries"
import {
  CreateTransformationMutation,
  UpdateTransformationMutation,
  DeleteTransformationMutation,
  TestTransformationMutation,
} from "@/lib/graphql/mutations"
import {
  transformationSchema,
  testTransformationSchema,
  type TransformationFormValues,
  type TestTransformationFormValues,
} from "@/lib/schemas"
import type { ResultOf } from "@/lib/graphql/types"

type TransformationItem = ResultOf<typeof TransformationsQuery>["transformations"]["nodes"][number]

const LIMIT = 25

const DEFAULT_CODE = `function handler(webhook) {
  return {
    payload: webhook.payload,
    headers: webhook.headers,
    eventType: webhook.eventType,
  };
}`

export function TransformationsPage() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: TransformationsQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })
  const [, createTransformation] = useMutation(CreateTransformationMutation)
  const [, updateTransformation] = useMutation(UpdateTransformationMutation)
  const [, deleteTransformation] = useMutation(DeleteTransformationMutation)

  const [createOpen, setCreateOpen] = useState(false)
  const [editItem, setEditItem] = useState<TransformationItem | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [testItem, setTestItem] = useState<TransformationItem | null>(null)

  const handleCreate = async (form: TransformationFormValues, envVarsInput?: Record<string, string>) => {
    const res = await createTransformation({
      input: {
        name: form.name,
        description: form.description || undefined,
        code: form.code,
        failOpen: form.failOpen,
        timeoutMs: form.timeoutMs,
        envVars: envVarsInput,
      },
    })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Transformation created")
    setCreateOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleUpdate = async (form: TransformationFormValues, envVarsInput?: Record<string, string>) => {
    if (!editItem) return
    const res = await updateTransformation({
      id: editItem.id,
      input: {
        name: form.name,
        description: form.description,
        code: form.code,
        enabled: form.enabled,
        failOpen: form.failOpen,
        timeoutMs: form.timeoutMs,
        envVars: envVarsInput,
      },
    })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Transformation updated")
    setEditItem(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleDelete = async () => {
    if (!deleteId) return
    const res = await deleteTransformation({ id: deleteId })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Transformation deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const selectedItem = expandedId
    ? (result.data?.transformations.nodes ?? []).find((n) => n.id === expandedId) ?? null
    : null

  const columns: Column<TransformationItem>[] = [
    {
      header: "Name",
      cell: (row) => (
        <span className="text-xs font-medium">{row.name}</span>
      ),
    },
    {
      header: "Description",
      cell: (row) => (
        <span className="text-xs text-muted-foreground max-w-80 truncate block">
          {row.description || "\u2014"}
        </span>
      ),
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.enabled ? "ACTIVE" : "INACTIVE"} />,
    },
    {
      header: "Fail Open",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">{row.failOpen ? "Yes" : "No"}</span>
      ),
    },
    {
      header: "Timeout",
      cell: (row) => (
        <span className="text-xs text-muted-foreground font-mono">{row.timeoutMs}ms</span>
      ),
    },
    {
      header: "Updated",
      cell: (row) => <TimeAgo date={row.updatedAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setTestItem(row) }}
            aria-label="Test"
          >
            <Play className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setEditItem(row) }}
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Transformations" description="JavaScript functions to transform webhook payloads">
        <Button size="sm" onClick={() => setCreateOpen(true)} className="shadow-primary-glow transition-shadow">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Transformation
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search transformations..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.transformations.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.transformations.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No transformations"
        emptyDescription="Create transformations to modify webhook payloads before delivery"
        onRowClick={(row) => setExpandedId(expandedId === row.id ? null : row.id)}
      />

      {selectedItem && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              <Code2 className="h-4 w-4 mr-1.5 inline" />
              {selectedItem.name}
            </CardTitle>
            {selectedItem.description && (
              <p className="text-sm text-muted-foreground">{selectedItem.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <pre className="rounded-lg bg-muted/50 p-4 overflow-x-auto text-xs font-mono leading-relaxed">
              {selectedItem.code}
            </pre>
          </CardContent>
        </Card>
      )}

      <TransformationFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Create Transformation"
        onSubmit={handleCreate}
      />

      {editItem && (
        <TransformationFormDialog
          open
          onOpenChange={() => setEditItem(null)}
          title="Edit Transformation"
          initial={{
            name: editItem.name,
            description: editItem.description,
            code: editItem.code,
            enabled: editItem.enabled,
            failOpen: editItem.failOpen,
            timeoutMs: editItem.timeoutMs,
          }}
          initialEnvVars={editItem.envVars as Record<string, string> | null | undefined}
          onSubmit={handleUpdate}
        />
      )}

      {testItem && (
        <TestTransformationDialog
          open
          onOpenChange={() => setTestItem(null)}
          code={testItem.code}
          name={testItem.name}
          envVars={testItem.envVars as Record<string, string> | null | undefined}
          timeoutMs={testItem.timeoutMs}
        />
      )}

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Transformation"
        description="This will permanently delete this transformation. Any subscriptions or endpoints using it will no longer transform payloads."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  )
}

type EnvVarEntry = { key: string; value: string; isExisting: boolean }

function TransformationFormDialog({
  open,
  onOpenChange,
  title,
  initial,
  initialEnvVars,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  initial?: TransformationFormValues
  initialEnvVars?: Record<string, string> | null
  onSubmit: (values: TransformationFormValues, envVarsInput?: Record<string, string>) => Promise<void>
}) {
  const form = useForm<TransformationFormValues>({
    resolver: zodResolver(transformationSchema) as Resolver<TransformationFormValues>,
    defaultValues: initial ?? { name: "", description: "", code: DEFAULT_CODE, failOpen: true, timeoutMs: 1000, enabled: true },
  })

  const [envVars, setEnvVars] = useState<EnvVarEntry[]>([])

  useEffect(() => {
    if (open) {
      form.reset(initial ?? { name: "", description: "", code: DEFAULT_CODE, failOpen: true, timeoutMs: 1000, enabled: true })
      if (initialEnvVars) {
        setEnvVars(Object.entries(initialEnvVars).map(([key, value]) => ({ key, value: String(value), isExisting: true })))
      } else {
        setEnvVars([])
      }
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const addEnvVar = () => setEnvVars((prev) => [...prev, { key: "", value: "", isExisting: false }])
  const removeEnvVar = (index: number) => setEnvVars((prev) => prev.filter((_, i) => i !== index))
  const updateEnvVar = (index: number, field: "key" | "value", val: string) => {
    setEnvVars((prev) => prev.map((entry, i) => i === index ? { ...entry, [field]: val, isExisting: field === "value" ? false : entry.isExisting } : entry))
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    const envVarsObj: Record<string, string> = {}
    for (const ev of envVars) {
      if (ev.key.trim()) envVarsObj[ev.key.trim()] = ev.value
    }
    const envVarsInput = Object.keys(envVarsObj).length > 0 ? envVarsObj : undefined
    await onSubmit(values as TransformationFormValues, envVarsInput)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="My transformation" {...form.register("name")} />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeoutMs">Timeout (ms)</Label>
              <Input id="timeoutMs" type="number" placeholder="1000" {...form.register("timeoutMs")} />
              {form.formState.errors.timeoutMs && (
                <p className="text-xs text-destructive">{form.formState.errors.timeoutMs.message}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input id="description" placeholder="What this transformation does" {...form.register("description")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="code">JavaScript Code</Label>
            <Textarea
              id="code"
              className="font-mono text-xs min-h-48"
              placeholder={DEFAULT_CODE}
              {...form.register("code")}
            />
            {form.formState.errors.code && (
              <p className="text-xs text-destructive">{form.formState.errors.code.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Export a <code className="font-mono text-xs">handler(webhook)</code> function that receives <code className="font-mono text-xs">{"{ payload, headers, eventType }"}</code> and returns the transformed object, or <code className="font-mono text-xs">null</code> to drop.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="failOpen"
                checked={form.watch("failOpen") ?? true}
                onCheckedChange={(v) => form.setValue("failOpen", v)}
              />
              <Label htmlFor="failOpen" className="text-sm">Fail open</Label>
              <span className="text-xs text-muted-foreground">(deliver original payload on error)</span>
            </div>
            {initial && (
              <div className="flex items-center gap-2">
                <Switch
                  id="enabled"
                  checked={form.watch("enabled") ?? true}
                  onCheckedChange={(v) => form.setValue("enabled", v)}
                />
                <Label htmlFor="enabled" className="text-sm">Enabled</Label>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Environment Variables</Label>
              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={addEnvVar}>
                <Plus className="h-3 w-3 mr-1" />Add Variable
              </Button>
            </div>
            {envVars.length > 0 && (
              <div className="space-y-2">
                {envVars.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="KEY"
                      className="font-mono text-xs flex-1"
                      value={entry.key}
                      onChange={(e) => updateEnvVar(index, "key", e.target.value)}
                    />
                    <Input
                      placeholder="value"
                      className="font-mono text-xs flex-1"
                      type={entry.isExisting ? "password" : "text"}
                      value={entry.value}
                      onChange={(e) => updateEnvVar(index, "value", e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeEnvVar(index)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Key-value pairs available to the transformation function as environment variables</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "..." : initial ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TestTransformationDialog({
  open,
  onOpenChange,
  code,
  name,
  envVars,
  timeoutMs,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  code: string
  name: string
  envVars?: Record<string, string> | null
  timeoutMs?: number
}) {
  const form = useForm<TestTransformationFormValues>({
    resolver: zodResolver(testTransformationSchema),
    defaultValues: { code, payload: '{\n  "event": "test"\n}', eventType: "test.event", timeoutMs: timeoutMs ?? 5000 },
  })

  const [, testTransformation] = useMutation(TestTransformationMutation)
  const [testResult, setTestResult] = useState<{ success: boolean; output?: Record<string, unknown> | null; error?: string | null; durationMs: number } | null>(null)

  useEffect(() => {
    if (open) {
      form.reset({ code, payload: '{\n  "event": "test"\n}', eventType: "test.event", timeoutMs: timeoutMs ?? 5000 })
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setTestResult(null)
  }

  const handleTest = form.handleSubmit(async (values) => {
    let payload: Record<string, unknown>
    try {
      payload = JSON.parse(values.payload)
    } catch {
      toast.error("Payload must be valid JSON")
      return
    }
    const res = await testTransformation({
      input: {
        code: values.code,
        payload,
        eventType: values.eventType,
        envVars: envVars ?? undefined,
        timeoutMs: values.timeoutMs || undefined,
      },
    })
    if (res.error) { toast.error(res.error.message); return }
    if (res.data) {
      setTestResult(res.data.testTransformation)
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test: {name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleTest} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-code">Code</Label>
            <Textarea
              id="test-code"
              className="font-mono text-xs min-h-40"
              {...form.register("code")}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="test-payload">Payload (JSON)</Label>
              <Textarea
                id="test-payload"
                className="font-mono text-xs min-h-28"
                {...form.register("payload")}
              />
              {form.formState.errors.payload && (
                <p className="text-xs text-destructive">{form.formState.errors.payload.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-eventType">Event Type</Label>
              <Input
                id="test-eventType"
                className="font-mono"
                {...form.register("eventType")}
              />
              <Label htmlFor="test-timeoutMs">Timeout (ms)</Label>
              <Input
                id="test-timeoutMs"
                type="number"
                className="font-mono"
                {...form.register("timeoutMs")}
              />
              {form.formState.errors.timeoutMs && (
                <p className="text-xs text-destructive">{form.formState.errors.timeoutMs.message}</p>
              )}
              {testResult && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={testResult.success ? "DELIVERED" : "FAILED"} />
                    <span className="text-xs text-muted-foreground font-mono">{testResult.durationMs}ms</span>
                  </div>
                  {testResult.error && (
                    <pre className="text-xs text-destructive bg-destructive/10 rounded-md p-2 whitespace-pre-wrap">{testResult.error}</pre>
                  )}
                  {testResult.output && (
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-medium mb-1 text-muted-foreground">Output</div>
                        <JsonViewer data={testResult.output} />
                      </div>
                      <div>
                        <div className="text-xs font-medium mb-1 text-muted-foreground">Diff (input → output)</div>
                        <JsonDiff
                          before={(() => { try { return JSON.parse(form.getValues("payload")) } catch { return {} } })()}
                          after={testResult.output}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              <Play className="h-3.5 w-3.5 mr-1.5" />
              {form.formState.isSubmitting ? "Running..." : "Run Test"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
